import { DropdownMenuItem } from "@arcadeai/design-system/components/ui/atoms/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@arcadeai/design-system/components/ui/atoms/sidebar";
import { Skeleton } from "@arcadeai/design-system/components/ui/atoms/skeleton";
import { useNavigate } from "@tanstack/react-router";
import { Rocket } from "lucide-react";
import { useGetSubscription } from "@/app/billing/api/hooks";
import type { PlanName } from "@/app/billing/types";
import { useGetCapabilities } from "@/app/capabilities/api/hooks";
import { useOrgProjectRouteContext } from "@/lib/org-project-context/hooks";

function UpgradeCardContent() {
  return (
    <div className="rounded-md bg-gradient-to-r from-orange-400/10 via-pink-500/10 to-blue-500/10 p-2">
      <div className="flex items-center gap-2">
        <Rocket className="size-4 text-black dark:text-white" />
        <p className="font-semibold text-black dark:text-white">
          Upgrade your plan
        </p>
      </div>
      <p className="mt-1 text-muted-foreground text-xs">
        Increase user limits, tool calls and get premium support.
      </p>
    </div>
  );
}

function UpgradeButtonSkeleton() {
  return (
    <SidebarMenu>
      <SidebarMenuItem className="my-3">
        <Skeleton className="h-10 w-full rounded-md" />
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

export function UpgradeButton() {
  const { state } = useSidebar();
  const { organizationId, projectId } = useOrgProjectRouteContext();

  const { data: capabilities, isPending: capabilitiesPending } =
    useGetCapabilities();
  const billingVisible = capabilities?.billing.enabled ?? false;

  const { data: subscription, isPending: subscriptionPending } =
    useGetSubscription(organizationId, { enabled: billingVisible });

  const navigate = useNavigate();

  if (capabilitiesPending || (billingVisible && subscriptionPending)) {
    return <UpgradeButtonSkeleton />;
  }
  if (!billingVisible) {
    return null;
  }

  const plan: PlanName =
    // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
    (subscription?.plan?.external_plan_id as PlanName) ?? "enterprise";

  if (plan !== "free") {
    return null;
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem
        onClick={() => {
          navigate({
            to: "/orgs/$organizationId/projects/$projectId/billing/upgrade",
            params: { organizationId, projectId },
          });
        }}
      >
        <SidebarMenuButton
          className="mb-2 h-auto cursor-pointer rounded-md p-0 hover:bg-muted/50 active:bg-muted/90"
          tooltip="Upgrade Plan"
        >
          {state === "collapsed" ? (
            <Rocket className="size-4" />
          ) : (
            <UpgradeCardContent />
          )}
        </SidebarMenuButton>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

export function UpgradeDropdownItem() {
  const navigate = useNavigate({
    from: "/orgs/$organizationId/projects/$projectId/billing/upgrade",
  });

  return (
    <DropdownMenuItem
      className="m-1 cursor-pointer rounded-md p-0"
      onClick={() => {
        navigate({
          to: ".",
        });
      }}
    >
      <UpgradeCardContent />
    </DropdownMenuItem>
  );
}
